/**
 * TimelineTab — Tab 4 (Aktivitaet) des Card-Detail-Modal (CARD-02, CARD-07).
 *
 * Zeigt alle Aktivitaeten zu einem Lead chronologisch absteigend, gruppiert
 * nach Tag-Label ('Heute', 'Gestern', 'vor X Tagen', 'dd.MM.yyyy') via
 * `groupEventsByDay`.
 *
 * Verantwortlich fuer:
 *  - Initial-Load aller Events via `leadsApi.events(leadId)`
 *  - Lazy-Load mit `visible`-Counter (initial 20, +20 pro Klick auf "Mehr laden")
 *  - Icon-Map fuer alle 13 LeadEventTypes (created, auto_move, manual_move,
 *    mail_sent, mail_received, reply_received, call_logged, note_changed,
 *    tag_added, tag_removed, assigned, task_created, task_completed)
 *  - Inline-CallLogForm (Toggle ueber "+ Telefonat erfassen"-Button)
 *  - Neue Telefonate landen sofort als prepended Event in der Liste (kein Reload)
 *
 * CI-Farben: #E9FF57 + #1A1A1A auf Action-Buttons.
 */

import { useEffect, useState } from 'react';
import {
  Plus,
  ArrowRight,
  Mail,
  MailOpen,
  Phone,
  Tag,
  FileText,
  User,
  MessageSquare,
  CheckSquare,
} from 'lucide-react';
import { leadsApi } from '../../services/leadsApi';
import { groupEventsByDay } from '../../lib/groupEvents';
import CallLogForm from '../CallLogForm';
import type { Lead, LeadEvent, LeadEventType } from '../../types';

interface Props {
  lead: Lead;
}

const PAGE_SIZE = 20;

interface EventDescriptor {
  Icon: typeof Plus;
  label: (e: LeadEvent) => string;
  color: string;
}

/**
 * EVENT_ICON — Map von eventType -> Icon + Label-Builder.
 *
 * PFLICHT: Enthaelt alle 13 LeadEventTypes aus
 * backend/src/models/LeadEvent.ts. Wenn das Backend neue Typen hinzufuegt,
 * MUSS diese Map erweitert werden — sonst greift FALLBACK_ICON (graues
 * FileText-Icon mit rohem Typ-Namen).
 */
const EVENT_ICON: Record<LeadEventType, EventDescriptor> = {
  created: {
    Icon: Plus,
    label: (e) =>
      `Lead angelegt aus ${e.metadata?.source ?? 'unbekannt'}`,
    color: 'text-green-600',
  },
  auto_move: {
    Icon: ArrowRight,
    label: (e) =>
      `Auto-Move von ${e.fromStatus ?? '?'} nach ${e.toStatus ?? '?'}`,
    color: 'text-blue-600',
  },
  manual_move: {
    Icon: ArrowRight,
    label: (e) =>
      `Manueller Move nach ${e.toStatus ?? '?'}`,
    color: 'text-blue-700',
  },
  mail_sent: {
    Icon: Mail,
    label: (e) => `Mail "${e.mailTemplate ?? 'custom'}" versendet`,
    color: 'text-gray-700',
  },
  mail_received: {
    Icon: MailOpen,
    label: () => 'E-Mail empfangen',
    color: 'text-gray-700',
  },
  reply_received: {
    Icon: MessageSquare,
    label: (e) => {
      const snippet = e.metadata?.snippet;
      if (typeof snippet === 'string' && snippet.length > 0) {
        return `Antwort empfangen: ${snippet.slice(0, 80)}`;
      }
      return 'Antwort empfangen';
    },
    color: 'text-purple-700',
  },
  call_logged: {
    Icon: Phone,
    label: (e) => {
      const duration = e.metadata?.durationMinutes ?? '?';
      const note = e.metadata?.resultNote ?? '';
      return note
        ? `Telefonat (${duration} min): ${note}`
        : `Telefonat (${duration} min)`;
    },
    color: 'text-green-700',
  },
  note_changed: {
    Icon: FileText,
    label: () => 'Notiz geaendert',
    color: 'text-gray-600',
  },
  tag_added: {
    Icon: Tag,
    label: (e) => `Tag hinzugefuegt: ${e.metadata?.tag ?? ''}`,
    color: 'text-orange-600',
  },
  tag_removed: {
    Icon: Tag,
    label: (e) => `Tag entfernt: ${e.metadata?.tag ?? ''}`,
    color: 'text-gray-500',
  },
  assigned: {
    Icon: User,
    label: (e) => `Zugewiesen an User #${e.metadata?.userId ?? '?'}`,
    color: 'text-blue-600',
  },
  task_created: {
    Icon: CheckSquare,
    label: (e) => `Task erstellt: ${e.metadata?.title ?? ''}`,
    color: 'text-yellow-700',
  },
  task_completed: {
    Icon: CheckSquare,
    label: (e) => `Task abgeschlossen: ${e.metadata?.title ?? ''}`,
    color: 'text-green-700',
  },
};

const FALLBACK_ICON: EventDescriptor = {
  Icon: FileText,
  label: (e) => e.eventType,
  color: 'text-gray-400',
};

function descriptorFor(eventType: string): EventDescriptor {
  return (
    (EVENT_ICON as Record<string, EventDescriptor>)[eventType] ??
    FALLBACK_ICON
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function actorLabel(e: LeadEvent): string {
  return e.actorUserId == null ? 'System' : `User #${e.actorUserId}`;
}

export default function TimelineTab({ lead }: Props) {
  const [events, setEvents] = useState<LeadEvent[]>([]);
  const [visible, setVisible] = useState<number>(PAGE_SIZE);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showCallForm, setShowCallForm] = useState<boolean>(false);

  // Initial-Load + Reload bei Lead-Wechsel
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setShowCallForm(false);
    setVisible(PAGE_SIZE);
    leadsApi
      .events(lead.id)
      .then((evs) => {
        if (!cancelled) setEvents(evs);
      })
      .catch((e: any) => {
        if (!cancelled) {
          setLoadError(e?.message ?? 'Aktivitaeten konnten nicht geladen werden');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lead.id]);

  const handleLogged = (newEvent: LeadEvent) => {
    setEvents((prev) => [newEvent, ...prev]);
    setShowCallForm(false);
  };

  const handleLoadMore = () => {
    setVisible((v) => v + PAGE_SIZE);
  };

  const visibleEvents = events.slice(0, visible);
  const buckets = groupEventsByDay(visibleEvents);
  const hasMore = visible < events.length;

  return (
    <div className="space-y-4">
      {/* Top-Bar: Telefonat erfassen */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[#1A1A1A]">Aktivitaet</h3>
        {!showCallForm && (
          <button
            type="button"
            onClick={() => setShowCallForm(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
          >
            <Phone className="w-4 h-4" />
            Telefonat erfassen
          </button>
        )}
      </div>

      {/* Inline CallLogForm */}
      {showCallForm && (
        <CallLogForm
          leadId={lead.id}
          onLogged={handleLogged}
          onCancel={() => setShowCallForm(false)}
        />
      )}

      {/* Loading / Error / Empty */}
      {loading && (
        <div className="text-sm text-gray-500">Lade Aktivitaeten...</div>
      )}
      {loadError && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          {loadError}
        </div>
      )}
      {!loading && !loadError && events.length === 0 && (
        <div className="text-sm text-gray-500 italic">
          Noch keine Aktivitaeten
        </div>
      )}

      {/* Timeline-Buckets */}
      {!loading && buckets.length > 0 && (
        <div className="space-y-4">
          {buckets.map((bucket) => (
            <section key={bucket.label}>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 pb-1 border-b border-[#DAD9D9]">
                {bucket.label}
              </h4>
              <ul className="space-y-2">
                {bucket.events.map((ev) => {
                  const { Icon, label, color } = descriptorFor(ev.eventType);
                  return (
                    <li
                      key={ev.id}
                      className="flex items-start gap-3 text-sm"
                    >
                      <span
                        className={`flex-shrink-0 mt-0.5 ${color}`}
                        aria-hidden="true"
                      >
                        <Icon className="w-4 h-4" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[#1A1A1A] break-words">
                          {label(ev)}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {formatTime(ev.createdAt)} · {actorLabel(ev)}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}

      {/* Lazy-Load: Mehr laden */}
      {hasMore && (
        <div className="flex justify-center pt-2">
          <button
            type="button"
            onClick={handleLoadMore}
            className="px-3 py-1.5 rounded text-sm font-medium border border-[#DAD9D9] text-[#1A1A1A] hover:bg-gray-50"
          >
            Mehr laden ({events.length - visible} weitere)
          </button>
        </div>
      )}
    </div>
  );
}
